import { useCallback, useEffect, useState } from 'react'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  ConversationsResponse,
  getConversationsPaginated,
  getConversationWithTasks
} from '~/services/conversationService'
import { ConversationWithTasks } from '~/types/Conversations'

interface UseConversationsState {
  conversations: ConversationWithTasks[]
  pages: ConversationsResponse[]
  nextCursor: number | null
  hasNextPage: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  error: Error | null
  status: 'idle' | 'pending' | 'success' | 'error'
  searchQuery: string
  searchType: 'chats' | 'tasks' | undefined
  // Credit limit enforcement
  dataHidden: boolean
  dataHiddenReason: 'credit_limit_exceeded' | null
  isFreeLicense: boolean
  creditsUsed: number
  creditsLimit: number
}

// Initialize search type from localStorage to avoid double fetch on initial load
function getInitialSearchType(): 'chats' | 'tasks' | undefined {
  if (typeof window === 'undefined') return undefined
  const stored = localStorage.getItem('curie-sidebar-view')
  if (stored === 'chats' || stored === 'tasks') return stored
  return 'chats' // Default to chats if not set
}

export function useConversations() {
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid
  const [state, setState] = useState<UseConversationsState>(() => ({
    conversations: [],
    pages: [],
    nextCursor: null,
    hasNextPage: false,
    isFetching: false,
    isFetchingNextPage: false,
    error: null,
    status: 'idle',
    searchQuery: '',
    searchType: getInitialSearchType(),
    // Credit limit enforcement
    dataHidden: false,
    dataHiddenReason: null,
    isFreeLicense: false,
    creditsUsed: 0,
    creditsLimit: 0
  }))

  // Function to fetch a page of conversations
  const fetchPage = useCallback(
    async (cursor?: number, isNextPage = false, query?: string) => {
      if (!workspaceUuid) {
        console.warn('[useConversations] No workspace UUID, skipping fetch page')
        return
      }

      try {
        // Set loading states
        setState(prev => ({
          ...prev,
          isFetching: true,
          isFetchingNextPage: isNextPage,
          status: prev.conversations.length === 0 ? 'pending' : prev.status,
          error: null
        }))

        const response = await getConversationsPaginated(workspaceUuid, {
          lastTimestamp: cursor,
          query: query ?? state.searchQuery,
          type: state.searchType
        })

        setState(prev => {
          const newPages = isNextPage ? [...prev.pages, response] : [response]
          const allConversations = newPages.flatMap(page => page.data || [])

          return {
            ...prev,
            conversations: allConversations,
            pages: newPages,
            nextCursor: response.next_cursor,
            hasNextPage: response.has_more,
            isFetching: false,
            isFetchingNextPage: false,
            status: 'success',
            error: null,
            // Credit limit enforcement
            dataHidden: response.data_hidden ?? false,
            dataHiddenReason: response.data_hidden_reason ?? null,
            isFreeLicense: response.is_free_license ?? false,
            creditsUsed: response.credits_used ?? 0,
            creditsLimit: response.credits_limit ?? 0
          }
        })
      } catch (error) {
        setState(prev => ({
          ...prev,
          isFetching: false,
          isFetchingNextPage: false,
          status: 'error',
          error: error as Error
        }))
      }
    },
    [workspaceUuid, state.searchQuery, state.searchType]
  )

  // Fetch next page function
  const fetchNextPage = useCallback(async (): Promise<ConversationsResponse | undefined> => {
    if (!workspaceUuid) {
      console.warn('[useConversations] No workspace UUID, skipping fetch next page')
      return undefined
    }

    if (!state.hasNextPage || state.isFetchingNextPage) {
      return undefined
    }

    // Set loading state for next page
    setState(prev => ({
      ...prev,
      isFetchingNextPage: true,
      error: null
    }))

    try {
      const response = await getConversationsPaginated(workspaceUuid, {
        lastTimestamp: state.nextCursor || undefined,
        query: state.searchQuery,
        type: state.searchType
      })

      setState(prev => {
        const newPages = [...prev.pages, response]
        const allConversations = newPages.flatMap(page => page.data || [])

        return {
          ...prev,
          conversations: allConversations,
          pages: newPages,
          nextCursor: response.next_cursor,
          hasNextPage: response.has_more,
          isFetching: false,
          isFetchingNextPage: false,
          status: 'success',
          error: null,
          // Credit limit enforcement (preserve from first response)
          dataHidden: prev.dataHidden || (response.data_hidden ?? false),
          dataHiddenReason: prev.dataHiddenReason || (response.data_hidden_reason ?? null),
          isFreeLicense: response.is_free_license ?? prev.isFreeLicense,
          creditsUsed: response.credits_used ?? prev.creditsUsed,
          creditsLimit: response.credits_limit ?? prev.creditsLimit
        }
      })

      return response
    } catch (error) {
      setState(prev => ({
        ...prev,
        isFetching: false,
        isFetchingNextPage: false,
        status: 'error',
        error: error as Error
      }))
      throw error
    }
  }, [
    workspaceUuid,
    state.hasNextPage,
    state.isFetchingNextPage,
    state.nextCursor,
    state.searchQuery,
    state.searchType
  ])

  // Refetch function (starts from beginning)
  const refetch = useCallback(async (): Promise<ConversationsResponse | undefined> => {
    if (!workspaceUuid) {
      console.warn('[useConversations] No workspace UUID, skipping refetch')
      return undefined
    }

    // Set loading state for refetch
    setState(prev => ({
      ...prev,
      isFetching: true,
      isFetchingNextPage: false,
      status: prev.conversations.length === 0 ? 'pending' : prev.status,
      error: null
    }))

    try {
      const response = await getConversationsPaginated(workspaceUuid, {
        query: state.searchQuery,
        type: state.searchType
      })

      setState(prev => ({
        ...prev,
        conversations: response.data || [],
        pages: [response],
        nextCursor: response.next_cursor,
        hasNextPage: response.has_more,
        isFetching: false,
        isFetchingNextPage: false,
        status: 'success',
        error: null,
        // Credit limit enforcement
        dataHidden: response.data_hidden ?? false,
        dataHiddenReason: response.data_hidden_reason ?? null,
        isFreeLicense: response.is_free_license ?? false,
        creditsUsed: response.credits_used ?? 0,
        creditsLimit: response.credits_limit ?? 0
      }))

      return response
    } catch (error) {
      setState(prev => ({
        ...prev,
        isFetching: false,
        isFetchingNextPage: false,
        status: 'error',
        error: error as Error
      }))
      throw error
    }
  }, [workspaceUuid, state.searchQuery, state.searchType])

  // Function to update or insert (upsert) a specific conversation
  const updateConversation = useCallback(
    async (identifier: string | number): Promise<void> => {
      if (!workspaceUuid) {
        console.warn('[useConversations] No workspace UUID, skipping update')
        return
      }

      try {
        let conversationUuid: string

        // If identifier is a number (conversation ID), find the UUID from existing conversations
        if (typeof identifier === 'number') {
          // Use functional getter to avoid stale closure
          let existingConversation: ConversationWithTasks | undefined
          setState(prev => {
            existingConversation = prev.conversations.find(conv => conv.id === identifier)
            return prev // Don't modify state
          })
          if (!existingConversation) {
            // Conversation not loaded yet — refetch the full list so the sidebar picks it up
            console.log(
              `[useConversations] Conversation ID ${identifier} not in state, triggering refetch`
            )
            await refetch()
            return
          }
          conversationUuid = existingConversation.uuid
        } else {
          // If identifier is a string, assume it's already a UUID
          conversationUuid = identifier
        }

        // Fetch the updated conversation
        const fetchedConversation = await getConversationWithTasks(workspaceUuid, conversationUuid)

        // Skip update if data is hidden (credit limit exceeded for users)
        // The API returns a minimal response without conversation data when hidden
        // Updating state with this broken data would corrupt the conversation list
        if ((fetchedConversation as unknown as { data_hidden?: boolean }).data_hidden) {
          console.log(
            `[useConversations] Skipping update for conversation ${conversationUuid}: data hidden`
          )
          return
        }

        const updatedConversation = fetchedConversation

        // Upsert the conversation in the state
        setState(prev => {
          // Check if conversation exists in the current state
          const existingIndex = prev.conversations.findIndex(
            conv => conv.uuid === conversationUuid || conv.id === updatedConversation.id
          )

          const existsInState = existingIndex !== -1

          let updatedConversations: ConversationWithTasks[]
          if (existsInState) {
            // Update existing conversation
            updatedConversations = prev.conversations.map((conv, index) =>
              index === existingIndex ? updatedConversation : conv
            )
          } else {
            // Insert new conversation at the beginning
            updatedConversations = [updatedConversation, ...prev.conversations]
          }

          // Update pages array - upsert in each page
          const updatedPages = prev.pages.map(page => {
            const pageExistingIndex = page.data.findIndex(
              conv => conv.uuid === conversationUuid || conv.id === updatedConversation.id
            )

            if (pageExistingIndex !== -1) {
              // Update existing conversation in page
              return {
                ...page,
                data: page.data.map((conv, index) =>
                  index === pageExistingIndex ? updatedConversation : conv
                )
              }
            }
            return page
          })

          // If conversation was not found in any page, add it to the first page
          const foundInPages = updatedPages.some(page =>
            page.data.some(
              conv => conv.uuid === conversationUuid || conv.id === updatedConversation.id
            )
          )

          let finalPages = updatedPages
          if (!foundInPages && finalPages.length > 0) {
            // Add to the first page
            finalPages = [
              {
                ...finalPages[0],
                data: [updatedConversation, ...finalPages[0].data]
              },
              ...finalPages.slice(1)
            ]
          } else if (!foundInPages && finalPages.length === 0) {
            // Create a new page if no pages exist
            finalPages = [
              {
                data: [updatedConversation],
                next_cursor: null,
                has_more: false
              }
            ]
          }

          console.log(
            `[useConversations] ${existsInState ? 'Updated' : 'Inserted'} conversation:`,
            conversationUuid
          )

          return {
            ...prev,
            conversations: updatedConversations,
            pages: finalPages
          }
        })
      } catch (error) {
        console.error('Failed to update conversation:', error)
        throw error
      }
    },
    [workspaceUuid, refetch] // Removed state.conversations dependency to prevent infinite loops
  )

  // Fetch conversations when workspace or search params change
  useEffect(() => {
    fetchPage(undefined, false, state.searchQuery)
  }, [fetchPage, state.searchQuery, state.searchType])

  // Public setter for search query
  const setSearchQuery = useCallback((query: string) => {
    setState(prev => {
      // Only update if query is actually different
      if (prev.searchQuery === query) {
        return prev
      }

      return {
        ...prev,
        searchQuery: query,
        // reset pagination on new search
        conversations: [],
        pages: [],
        nextCursor: null,
        hasNextPage: false
      }
    })
  }, [])

  // Public setter for search type (chats vs tasks)
  const setSearchType = useCallback((type: 'chats' | 'tasks' | undefined) => {
    setState(prev => {
      // Only update if type is actually different
      if (prev.searchType === type) {
        return prev
      }

      return {
        ...prev,
        searchType: type,
        // reset pagination on type change
        conversations: [],
        pages: [],
        nextCursor: null,
        hasNextPage: false
      }
    })
  }, [])

  // Add a new conversation to the beginning of the list
  const addConversation = useCallback((conversation: ConversationWithTasks): void => {
    setState(prev => {
      // Prepend the new conversation to the conversations list
      const updatedConversations = [conversation, ...prev.conversations]

      // Update pages array - add to the first page
      let updatedPages = prev.pages
      if (updatedPages.length > 0) {
        // Add to the first page
        updatedPages = [
          {
            ...updatedPages[0],
            data: [conversation, ...updatedPages[0].data]
          },
          ...updatedPages.slice(1)
        ]
      } else {
        // Create a new page if no pages exist
        updatedPages = [
          {
            data: [conversation],
            next_cursor: null,
            has_more: false
          }
        ]
      }

      return {
        ...prev,
        conversations: updatedConversations,
        pages: updatedPages
      }
    })

    console.log('[useConversations] Added conversation to list:', conversation.uuid)
  }, [])

  // Upsert a conversation: update if exists, add if not
  // This is used to sync data from CurrentConversationContext to the sidebar
  const upsertConversation = useCallback((conversation: ConversationWithTasks): void => {
    // Skip if data is hidden (shouldn't happen as CurrentConversationContext guards this,
    // but be defensive)
    if ((conversation as unknown as { data_hidden?: boolean }).data_hidden) {
      console.log('[useConversations] Skipping upsert: data hidden')
      return
    }

    setState(prev => {
      const existingIndex = prev.conversations.findIndex(
        conv => conv.uuid === conversation.uuid || conv.id === conversation.id
      )

      const normalizedConversation = conversation

      let updatedConversations: ConversationWithTasks[]
      if (existingIndex !== -1) {
        // Update existing
        updatedConversations = prev.conversations.map((conv, index) =>
          index === existingIndex ? normalizedConversation : conv
        )
      } else {
        // Add to beginning
        updatedConversations = [normalizedConversation, ...prev.conversations]
      }

      // Update pages array
      const updatedPages = prev.pages.map(page => {
        const pageIndex = page.data.findIndex(
          conv => conv.uuid === normalizedConversation.uuid || conv.id === normalizedConversation.id
        )
        if (pageIndex !== -1) {
          return {
            ...page,
            data: page.data.map((conv, index) =>
              index === pageIndex ? normalizedConversation : conv
            )
          }
        }
        return page
      })

      // If not found in any page and we have pages, add to first page
      const foundInPages = updatedPages.some(page =>
        page.data.some(
          conv => conv.uuid === normalizedConversation.uuid || conv.id === normalizedConversation.id
        )
      )

      let finalPages = updatedPages
      if (!foundInPages) {
        if (finalPages.length > 0) {
          finalPages = [
            { ...finalPages[0], data: [normalizedConversation, ...finalPages[0].data] },
            ...finalPages.slice(1)
          ]
        } else {
          finalPages = [{ data: [normalizedConversation], next_cursor: null, has_more: false }]
        }
      }

      return {
        ...prev,
        conversations: updatedConversations,
        pages: finalPages
      }
    })
  }, [])

  // Optimistically merge partial conversation fields from a WebSocket event into state.
  // Zero API calls — the WS event already contains the data we need.
  const mergeConversationFields = useCallback(
    (fields: {
      uuid?: string
      id?: number
      title?: string
      conversation_metadata?: Record<string, unknown>
      created_time?: string
      updated_time?: string
      forceTop?: boolean
    }) => {
      setState(prev => {
        const idx = prev.conversations.findIndex(
          c => (fields.uuid && c.uuid === fields.uuid) || (fields.id && c.id === fields.id)
        )
        if (idx === -1) {
          if (!fields.uuid || !fields.id) return prev

          const createdTime = fields.created_time ?? fields.updated_time ?? new Date().toISOString()
          const inserted: ConversationWithTasks = {
            id: fields.id,
            uuid: fields.uuid,
            title: fields.title ?? null,
            userid: 0,
            created_time: createdTime,
            updated_time: fields.updated_time ?? createdTime,
            conversation_metadata: fields.conversation_metadata ?? {},
            tasks: [],
            is_processing:
              fields.conversation_metadata && 'is_processing' in fields.conversation_metadata
                ? Boolean(fields.conversation_metadata.is_processing)
                : false
          }

          const updatedPages =
            prev.pages.length > 0
              ? [
                  { ...prev.pages[0], data: [inserted, ...prev.pages[0].data] },
                  ...prev.pages.slice(1)
                ]
              : [{ data: [inserted], next_cursor: null, has_more: false }]

          return {
            ...prev,
            conversations: [inserted, ...prev.conversations],
            pages: updatedPages
          }
        }

        const merged: ConversationWithTasks = { ...prev.conversations[idx] }
        if (fields.title !== undefined) merged.title = fields.title
        if (fields.updated_time !== undefined) merged.updated_time = fields.updated_time
        if (fields.conversation_metadata && 'is_processing' in fields.conversation_metadata) {
          merged.is_processing = Boolean(fields.conversation_metadata.is_processing)
        }

        const matcher = (c: ConversationWithTasks) =>
          (fields.uuid && c.uuid === fields.uuid) || (fields.id && c.id === fields.id)

        return {
          ...prev,
          conversations: (() => {
            const updated = prev.conversations.map((c, i) => (i === idx ? merged : c))
            if (fields.forceTop) {
              return [merged, ...updated.filter((_, i) => i !== idx)]
            }
            return updated
          })(),
          pages: prev.pages.map(page => ({
            ...page,
            data: page.data.map(c => (matcher(c) ? merged : c))
          }))
        }
      })
    },
    []
  )

  // Optimistically merge partial task fields from a WebSocket event into state.
  // Finds the matching conversation by ID, then the matching task by task_id.
  const mergeTaskFields = useCallback(
    (fields: {
      task_id: number
      conversation_id: number
      status?: string
      cmd_type?: string
      updated_ts?: string
    }) => {
      setState(prev => {
        const conversationId = Number(fields.conversation_id)
        const taskId = Number(fields.task_id)
        if (!Number.isFinite(conversationId) || !Number.isFinite(taskId)) return prev

        const convIdx = prev.conversations.findIndex(c => Number(c.id) === conversationId)
        if (convIdx === -1) return prev
        const conv = prev.conversations[convIdx]
        if (!conv.tasks?.length) return prev

        const taskIdx = conv.tasks.findIndex(t => Number(t.task_id) === taskId)
        if (taskIdx === -1) return prev

        const updatedTask = { ...conv.tasks[taskIdx] }
        if (fields.status !== undefined) updatedTask.status = fields.status
        if (fields.cmd_type) updatedTask.cmd_type = fields.cmd_type
        if (fields.updated_ts !== undefined) updatedTask.updated_ts = fields.updated_ts

        const updatedConv: ConversationWithTasks = {
          ...conv,
          tasks: conv.tasks.map((t, i) => (i === taskIdx ? updatedTask : t))
        }

        return {
          ...prev,
          conversations: prev.conversations.map((c, i) => (i === convIdx ? updatedConv : c)),
          pages: prev.pages.map(page => ({
            ...page,
            data: page.data.map(c => (Number(c.id) === conversationId ? updatedConv : c))
          }))
        }
      })
    },
    []
  )

  const isLoading = state.isFetching

  return {
    conversations: state.conversations,
    fetchNextPage: fetchNextPage as () => Promise<ConversationsResponse>,
    hasNextPage: state.hasNextPage,
    isFetching: state.isFetching,
    isFetchingNextPage: state.isFetchingNextPage,
    error: state.error,
    status: state.status,
    isLoading,
    refetch: refetch as () => Promise<unknown>,
    updateConversation,
    addConversation,
    upsertConversation,
    mergeConversationFields,
    mergeTaskFields,
    setSearchQuery,
    searchQuery: state.searchQuery,
    setSearchType,
    searchType: state.searchType,
    // Credit limit enforcement
    dataHidden: state.dataHidden,
    dataHiddenReason: state.dataHiddenReason,
    isFreeLicense: state.isFreeLicense,
    creditsUsed: state.creditsUsed,
    creditsLimit: state.creditsLimit
  }
}
